/**
 * Place → coordinates
 *
 * Searches the bundled city chunks; falls back to the Worker's Nominatim proxy
 * only on an explicit user action, and says so in the UI before making a network
 * call. Manual latitude/longitude entry is always available and is the only path
 * that is guaranteed offline.
 *
 * ── Three things this module has to get right ────────────────────────────────
 *
 * ⚠ **Historical names.** A birth record says "Allahabad", "Bombay", "Poona".
 * GeoNames stores Prayagraj, Mumbai, Pune. Every chunk carries an `alt` index of
 * historical and English spellings for exactly this reason, and a search that
 * only looked at primary names would fail on most pre-1970 Indian births.
 *
 * ⚠ **Ranking, not just matching.** "Baroda" is an alternate name of Vadodara
 * (1.8 M, Gujarat) *and* the primary name of a village in Madhya Pradesh
 * (18 000). Letting an exact primary-name match win outright puts a 1900 Baroda
 * birth 400 km away in the wrong state. Primary and alternate matches are
 * therefore pooled and ranked together, with population as the main term.
 *
 * ⚠ **Missing places are normal and must be said so.** GeoNames records
 * population 0 for 550,854 of the 557,995 populated places it lists in India, so
 * no threshold reaches village level — a village birthplace often simply is not
 * in the table. `search` reports that state distinctly from "no such place", so
 * the UI can point at manual coordinate entry instead of showing an empty list.
 *
 * © Manish Jagdish Thatte
 */

import { CITIES_DEFAULT_CHUNK, GEOCODE_ENDPOINT } from '../config.js';

/**
 * Chunks are fetched lazily and cached for the session.
 *
 * ◆ Dynamic `import()` rather than `fetch` so the bundler emits each chunk as its
 * own hashed asset with a long cache life, and so a file:// copy of the site still
 * works — which matters because "works fully with the network off" is an
 * acceptance criterion, not an aspiration.
 */
const loaded = new Map();
let indexPromise = null;

/** The chunk index: which countries live in which chunk. */
export async function citiesIndex() {
  indexPromise ??= import('../data/cities/cities-index.json').then((m) => m.default ?? m);
  return indexPromise;
}

/** Load one chunk, or return it from cache. */
export async function loadChunk(id) {
  if (loaded.has(id)) return loaded.get(id);
  const promise = import(`../data/cities/cities-${id}.json`)
    .then((m) => prepare(id, m.default ?? m))
    .catch((err) => {
      loaded.delete(id);
      throw new Error(`geocode: could not load city chunk "${id}": ${err.message}`);
    });
  loaded.set(id, promise);
  return promise;
}

/**
 * Turn a columnar chunk into something searchable, once per chunk.
 *
 * ◆ The lowercased name array is built here rather than per keystroke. With
 * ~8 800 places in the largest chunk, lowercasing on every search would be
 * ~8 800 allocations per typed character.
 */
function prepare(id, doc) {
  const { n, a, c, y, x, z, p } = doc.rows;
  const lower = n.map((s) => s.toLowerCase());

  // row index → its alternate spellings, and the inverse for lookup.
  const altByRow = new Map();
  const altIndex = [];
  for (const [row, names] of Object.entries(doc.alt ?? {})) {
    const r = Number(row);
    altByRow.set(r, names);
    for (const name of names) altIndex.push([name.toLowerCase(), r, name]);
  }

  return {
    id,
    count: n.length,
    zones: doc.zones,
    countries: doc.countries,
    admin1: doc.admin1,
    rows: { n, a, c, y, x, z, p },
    lower,
    altByRow,
    altIndex
  };
}

/** One row of a chunk as a plain place object. */
function placeAt(chunk, row, { matchedAs = null, via = 'name' } = {}) {
  const { n, a, c, y, x, z, p } = chunk.rows;
  return {
    name: n[row],
    admin1: chunk.admin1[a[row]] || '',
    country: chunk.countries[c[row]],
    lat: y[row],
    lonEast: x[row],
    timezone: chunk.zones[z[row]],
    // Stored in thousands; exposed as people.
    population: p[row] * 1000,
    alternateNames: chunk.altByRow.get(row) ?? [],
    matchedAs: matchedAs ?? n[row],
    via,
    chunk: chunk.id,
    row
  };
}

/**
 * Search the loaded chunks.
 *
 * Returns `{ results, searched, missingHint }`. `missingHint` is set when the
 * query found nothing *and* the chunks searched are the ones that would have held
 * it — which is when the UI should offer manual coordinates rather than an empty
 * list.
 *
 * `chunks` defaults to India alone. Pass `'all'` to load every chunk, which is
 * ~580 KB gzipped and must be an explicit user action, never automatic.
 */
export async function search(query, { chunks = [CITIES_DEFAULT_CHUNK], limit = 20 } = {}) {
  const q = String(query ?? '').trim().toLowerCase();
  if (q.length < 2) return { results: [], searched: [], missingHint: null };

  const index = await citiesIndex();
  const ids = chunks === 'all' ? Object.keys(index.chunks) : chunks;
  const parts = await Promise.all(ids.map(loadChunk));

  const hits = [];
  for (const chunk of parts) {
    // Primary names.
    for (let row = 0; row < chunk.lower.length; row++) {
      const score = matchScore(chunk.lower[row], q);
      if (score > 0) hits.push({ chunk, row, score, via: 'name', matchedAs: chunk.rows.n[row] });
    }
    // Alternate and historical names.
    for (const [low, row, original] of chunk.altIndex) {
      const score = matchScore(low, q);
      // ◆ A historical-name match is not penalised. "Bombay" is not a worse way
      // to name Mumbai for a 1935 birth — it is the correct way.
      if (score > 0) hits.push({ chunk, row, score, via: 'alternate', matchedAs: original });
    }
  }

  // Keep the best hit per place, then rank.
  const best = new Map();
  for (const h of hits) {
    const key = `${h.chunk.id}:${h.row}`;
    const prev = best.get(key);
    if (!prev || h.score > prev.score) best.set(key, h);
  }

  const ranked = [...best.values()].sort((A, B) => {
    if (B.score !== A.score) return B.score - A.score;
    // ⚠ Population is the tie-break that keeps Vadodara ahead of the village
    // also called Baroda. Without it an exact primary match on a hamlet beats an
    // alternate-name match on the city the record actually means.
    return B.chunk.rows.p[B.row] - A.chunk.rows.p[A.row];
  });

  const results = ranked.slice(0, limit)
    .map((h) => placeAt(h.chunk, h.row, { matchedAs: h.matchedAs, via: h.via }));

  return {
    results,
    searched: ids,
    missingHint: results.length === 0 ? missingHintFor(index, ids) : null
  };
}

/**
 * How well a candidate name matches a query. 0 means no match.
 *
 * ◆ Exact beats prefix beats word-start beats substring. Deliberately simple and
 * not a fuzzy matcher: a place picker that "helpfully" matches Pune for "Puna"
 * and also for "Puri" is worse than one that finds nothing and says so, because a
 * wrong place is a wrong chart and the user has no way to tell.
 */
function matchScore(candidate, q) {
  if (candidate === q) return 100;
  if (candidate.startsWith(q)) return 80;
  const wordStart = candidate.indexOf(` ${q}`);
  if (wordStart >= 0) return 60;
  if (candidate.includes(q)) return 40;
  return 0;
}

function missingHintFor(index, searchedIds) {
  const all = Object.keys(index.chunks);
  const unsearched = all.filter((id) => !searchedIds.includes(id));
  return {
    searched: searchedIds,
    unsearched,
    // The coverage fact, so the UI does not have to hard-code it.
    coverage: index._coverage ?? null,
    canWidenSearch: unsearched.length > 0,
    suggestManualCoordinates: true
  };
}

/** Every place whose primary or alternate name matches exactly, ranked by size. */
export async function resolveExact(name, { chunks = 'all' } = {}) {
  const { results } = await search(name, { chunks, limit: 50 });
  return results.filter((r) => r.matchedAs.toLowerCase() === String(name).trim().toLowerCase());
}

/**
 * Which chunk holds a country, so a search can widen to just the right one.
 */
export async function chunkForCountry(iso2) {
  const index = await citiesIndex();
  return index.countryChunk[String(iso2).toUpperCase()] ?? null;
}

/**
 * A manually entered latitude and longitude as a place.
 *
 * ◆ Always available, and the only fully-offline path. ⚠ `lonEast` is
 * east-positive; the caller is responsible for having asked the user which
 * hemisphere, because a sign error here is the single most damaging input mistake
 * available — it mirrors the chart's whole east-west geometry.
 */
export function manualPlace({ lat, lonEast, timezone, name = 'Manual coordinates' }) {
  if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
    throw new RangeError(`manualPlace: latitude ${lat} out of range −90…90`);
  }
  if (!Number.isFinite(lonEast) || lonEast < -180 || lonEast > 180) {
    throw new RangeError(`manualPlace: lonEast ${lonEast} out of range −180…180`);
  }
  return {
    name, admin1: '', country: '', lat, lonEast,
    timezone: timezone ?? null,
    population: null, alternateNames: [], matchedAs: name, via: 'manual',
    chunk: null, row: null
  };
}

/**
 * The online fallback. **Never called automatically.**
 *
 * ⚠ The whole site otherwise computes in the browser and sends nothing anywhere.
 * That property is worth more than the convenience of always finding a place, so
 * this requires an explicit user action and the UI must say a network request is
 * about to happen *before* calling it. `signal` is required so the user can
 * cancel.
 */
export async function searchOnline(query, { signal, endpoint = GEOCODE_ENDPOINT } = {}) {
  const url = `${endpoint}?q=${encodeURIComponent(query)}`;
  const res = await fetch(url, { signal, headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`geocode: the lookup service returned ${res.status}`);
  const data = await res.json();
  return (Array.isArray(data) ? data : data.results ?? []).map((r) => ({
    name: r.name ?? r.display_name ?? 'Unknown',
    admin1: r.admin1 ?? '',
    country: r.country ?? '',
    lat: Number(r.lat),
    // ⚠ Nominatim calls it `lon` and it is east-positive, which matches us. Do
    // not negate it.
    lonEast: Number(r.lon ?? r.lonEast),
    timezone: r.timezone ?? null,
    population: r.population ?? null,
    alternateNames: [],
    matchedAs: r.name ?? query,
    via: 'network',
    chunk: null, row: null
  }));
}
